/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    Field,
    Grid,
    Spacer,
    Text,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
    ApiResponse,
    Button,
    InfoDialog,
    Input,
    TextArea,
    createOrderThunk,
    useAppDispath,
} from 'shared';
import BaseError from 'utils/base-error';
import { useCart } from 'features';
import { PatternFormat } from 'react-number-format';
import { OrderCreateForm } from 'entities/order';
import { z } from 'zod';
// eslint-disable-next-line no-restricted-imports
import { CreateOrderResponseSchema } from 'shared/api/create-order';
import ym from 'react-yandex-metrika';
// eslint-disable-next-line no-restricted-imports
import { Modal } from 'shared/ui/Modal';
import { ICartFullItem } from '../../lib/types/cart-full-item';

const createOrderItemUrl = (type: 'delivery' | 'culinary-studio' | 'outdoor-bars' | 'sweet-shop' | 'cake-to-order', uid: string) => {
    let entityPath = '';

    if (type === 'delivery') {
        entityPath = 'delivery';
    } else if (type === 'culinary-studio') {
        entityPath = 'culinary-studio';
    } else if (type === 'outdoor-bars') {
        entityPath = 'outdoor-bars';
    } else if (type === 'cake-to-order') {
        entityPath = 'sweet-shop/articles-to-order';
    } else {
        entityPath = 'sweet-shop/articles';
    }

    return `https://corsocatering.ru/${entityPath}/${uid}`;
};

interface FormValues {
    name: string
    phone: string
    email: string
    datetime: string
    address: string
    comment: string
  }

type CartPageOrderModalProps = {
    open: boolean
    setOpen: (v: boolean) => void
    cartItems: ICartFullItem[]
}

export const CartPageOrderModal: React.FC<CartPageOrderModalProps> = ({
    open,
    setOpen,
    cartItems,
}) => {
    const dispatch = useAppDispath();
    const { clearItems } = useCart();

    const [open2, setOpen2] = useState(false);
    const [title2, setTitle2] = useState('');
    const [text, setText] = useState('');
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>();

    const onSubmit = handleSubmit(async (data) => {
        const orderId = Date.now().toString();

        const response = await dispatch(
            createOrderThunk({
                name: data.name,
                phone: data.phone,
                email: data.email,
                date: Date.parse(data.datetime),
                address: data.address,
                comment: data.comment,
                items: cartItems.map(({
                    type,
                    uid,
                    title,
                    price,
                    count,
                }) => ({
                    name: title,
                    count,
                    price,
                    url: createOrderItemUrl(type, uid),
                })),
            }),
        );
        setOpen(false);
        if (response.payload instanceof BaseError) {
            setTitle2('Ошибка при отправке');
            setText('Обновите страницу или попробуйте позже');
        } else {
            const data2 = response.payload as
            ApiResponse<z.infer<typeof CreateOrderResponseSchema>>;

            setTitle2('Благодарим за заказ!');
            setText(`Номер заказа #${data2.data.data.id}. Наши специалисты свяжутся с вами в ближайшее время`);
            ym('reachGoal', 'corso-catering.ru/order');
            clearItems();
        }
        setOpen2(true);
    });

    const totalPrice = useMemo(() => {
        let res = 0;
        cartItems.forEach(({ price }) => {
            if (price) res += price;
        });
        return res;
    }, [JSON.stringify(cartItems)]);

    return (
        <>
            <Modal
                title="Оформление заказа"
                open={open}
                onChange={setOpen}
            >
                <form onSubmit={onSubmit}>
                    <Grid gap="2rem">
                        <Grid gap="0.5rem" color="#000000">
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: 'Поле не может быть пустым',
                                }}
                                render={({ field }) => (
                                    <Field.Root invalid={errors.name}>
                                        <Field.Label>
                                            <Text
                                                fontSize="1rem"
                                                fontWeight="bold"
                                            >
                                                Имя
                                            </Text>
                                        </Field.Label>
                                        <Input
                                            {...field}
                                            placeholder="Иван Иванович"
                                            inputMode="text"
                                            height="100%"
                                        />
                                        <Field.ErrorText fontSize="1rem">
                                            {errors.name?.message}
                                        </Field.ErrorText>
                                    </Field.Root>
                                )}
                            />
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    required: 'Поле не может быть пустым',
                                }}
                                render={({ field }) => (
                                    <Field.Root invalid={errors.phone}>
                                        <Field.Label>
                                            <Text
                                                fontSize="1rem"
                                                fontWeight="bold"
                                            >
                                                Номер телефона
                                            </Text>
                                        </Field.Label>
                                        <Input
                                            {...field}
                                            placeholder="Номер телефона"
                                            inputMode="tel"
                                            height="100%"
                                        />
                                        <Field.ErrorText fontSize="1rem">
                                            {errors.phone?.message}
                                        </Field.ErrorText>
                                    </Field.Root>
                                )}
                            />

                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: 'Поле не может быть пустым',
                                }}
                                render={({ field }) => (
                                    <Field.Root invalid={errors.email}>
                                        <Field.Label>
                                            <Text
                                                fontSize="1rem"
                                                fontWeight="bold"
                                            >
                                                Почта
                                            </Text>
                                        </Field.Label>
                                        <Input
                                            {...field}
                                            placeholder="example@mail.ru"
                                            inputMode="text"
                                            height="100%"
                                        />
                                        <Field.ErrorText fontSize="1rem">
                                            {errors.email?.message}
                                        </Field.ErrorText>
                                    </Field.Root>
                                )}
                            />

                            <Controller
                                name="datetime"
                                control={control}
                                rules={{
                                    required: 'Поле не может быть пустым',
                                }}
                                render={({ field }) => (
                                    <Field.Root invalid={errors.datetime}>
                                        <Field.Label>
                                            <Text
                                                fontSize="1rem"
                                                fontWeight="bold"
                                            >
                                                Дата и время доставки
                                            </Text>
                                        </Field.Label>
                                        <Input
                                            {...field}
                                            placeholder="example@mail.ru"
                                            type="date"
                                            height="100%"
                                        />
                                        <Field.ErrorText fontSize="1rem">
                                            {errors.datetime?.message}
                                        </Field.ErrorText>
                                    </Field.Root>
                                )}
                            />

                            <Controller
                                name="address"
                                control={control}
                                rules={{
                                    required: 'Поле не может быть пустым',
                                }}
                                render={({ field }) => (
                                    <Field.Root invalid={errors.address}>
                                        <Field.Label>
                                            <Text
                                                fontSize="1rem"
                                                fontWeight="bold"
                                            >
                                                Адрес мероприятия
                                            </Text>
                                        </Field.Label>
                                        <Input
                                            {...field}
                                            placeholder="Москва, ул. Зорге, 7г"
                                            height="100%"
                                        />
                                        <Field.ErrorText fontSize="1rem">
                                            {errors.address?.message}
                                        </Field.ErrorText>
                                    </Field.Root>
                                )}
                            />

                            <Controller
                                name="comment"
                                control={control}
                                render={({ field }) => (
                                    <Field.Root invalid={errors.comment}>
                                        <Field.Label>
                                            <Text
                                                fontSize="1rem"
                                                fontWeight="bold"
                                            >
                                                Комментарии к заказу
                                            </Text>
                                        </Field.Label>
                                        <TextArea
                                            placeholder="Хочется чтобы ..."
                                            {...field}
                                        />
                                        <Field.ErrorText fontSize="1rem">
                                            {errors.comment?.message}
                                        </Field.ErrorText>
                                    </Field.Root>
                                )}
                            />
                        </Grid>
                        <Grid
                            templateColumns="1fr auto"
                            gap="2rem"
                        >
                            <Text fontSize="1.5rem">
                                Сумма заказа:
                            </Text>
                            <Text fontWeight="bold" fontSize="2rem">
                                {`${totalPrice.toLocaleString()} р`}
                            </Text>
                        </Grid>
                        <Button
                            background="#E74C3C"
                            onClick={onSubmit}
                        >
                            <Text fontWeight="bold" fontSize="1.5rem">
                                Подтвердить
                            </Text>
                        </Button>
                    </Grid>
                </form>
            </Modal>
            <Modal
                title={title2}
                open={open2}
                onChange={setOpen2}
            >
                <Grid gap="1rem">
                    <Text>{text}</Text>
                    <Grid templateColumns="2fr 1fr" gap="1rem">
                        <Spacer />
                        <Button
                            onClick={() => setOpen2(false)}
                        >
                            <Text fontWeight="bold" fontSize="1.5rem">
                                Закрыть
                            </Text>
                        </Button>
                    </Grid>
                </Grid>
            </Modal>
        </>
    );
};
